import { isRef, onUnmounted } from 'vue'
import { useActivityTracking } from '../useActivityTracking.js'

/**
 * Fabrique les gestionnaires de filtre d'une page liste plutôt que de les
 * écrire un par un.
 *
 * Poser un filtre suppose toujours les trois mêmes gestes, dans cet ordre :
 * écrire la valeur, REVENIR À LA PAGE 1, recharger. Le deuxième est une règle
 * de liste, pas une règle métier, et c'est celui qu'on oublie — l'oubli ne
 * produit aucune erreur : l'utilisateur reste sur la page 7 d'un résultat qui
 * n'en compte plus que 2, et voit une liste vide alors que son filtre a
 * parfaitement fonctionné.
 *
 * Écrit à la main, ce triptyque représentait une quarantaine de lignes
 * identiques par page (six handlers dans QuotesList, six dans
 * useFormateursList), donc six occasions de se tromper par page.
 *
 * La recherche est le seul champ à part : elle passe par `onSearch`, qui porte
 * le report de saisie (cf. useDebouncedListSearch) — recharger à chaque frappe
 * inonderait le serveur.
 *
 * TOUT AUTRE FILTRE PEUT DEMANDER LE MÊME REPORT, par `set(clé, valeur,
 * { differe: true })`. C'est ce qu'exigent les CURSEURS : tirer une poignée de
 * cinq kilomètres à deux cents traverse quarante crans, donc quarante
 * rechargements dont trente-neuf ne seront jamais regardés. La valeur est
 * écrite TOUT DE SUITE — l'écran doit suivre le doigt — seul le chargement
 * attend. Les pages posaient ce report elles-mêmes, avec leur propre minuteur
 * jamais nettoyé au démontage : un rechargement partait trois cents
 * millisecondes après qu'on ait quitté la page.
 *
 * @param {{ filters: Object, currentPage?: import('vue').Ref<number> }} state
 *   `filters` accepte aussi bien un objet réactif qu'une ref d'objet.
 * @param {{
 *   load: () => any,
 *   onSearch?: (value: any) => any,
 *   track?: string,
 *   cascade?: Object<string, string[]>
 * }} actions
 *   track = contexte du panneau d'activité ; cascade = filtres à vider quand
 *   un autre change (une compétence n'a plus de sens hors de sa catégorie).
 * @returns {{ set: (key: string, value: any) => void, handlers: Object }}
 */
/** Report d'un filtre « différé », aligné sur celui de la recherche. */
const DELAI_DIFFERE = 300

export function useListFilterHandlers(state, actions) {
  const { filters, currentPage = null } = state
  const { load, onSearch = null, track = null, cascade = null } = actions
  const { trackFilter } = useActivityTracking()

  const target = () => (isRef(filters) ? filters.value : filters)

  let minuteurDiffere = null
  const annuler = () => {
    clearTimeout(minuteurDiffere)
    minuteurDiffere = null
  }
  onUnmounted(annuler)

  const set = (key, value, { differe = false } = {}) => {
    const current = target()
    current[key] = value

    // Un filtre dépendant d'un autre est vidé quand son parent change.
    const dependents = cascade?.[key]
    if (dependents) dependents.forEach(dependent => { current[dependent] = '' })

    // Le résultat repart de zéro : la page courante n'existe peut-être plus.
    if (currentPage) currentPage.value = 1

    if (key === 'search' && onSearch) {
      onSearch(value)
      return
    }

    if (track) trackFilter({ ...current }, track)

    annuler()
    if (!differe) {
      load()
      return
    }
    minuteurDiffere = setTimeout(() => {
      minuteurDiffere = null
      load()
    }, DELAI_DIFFERE)
  }

  // Un gestionnaire par clé de `filters`, plus un repli pour les clés posées
  // après coup (filtre ajouté dynamiquement, vue rapide) : le template branche
  // `@update:x="handlers.x"` sans que la page ait rien à déclarer.
  const cache = {}
  const handlerFor = (key) => {
    cache[key] ||= (value) => set(key, value)
    return cache[key]
  }
  Object.keys(target()).forEach(handlerFor)

  const handlers = new Proxy(cache, {
    get: (targetCache, key) => (typeof key === 'string' ? handlerFor(key) : targetCache[key])
  })

  return { set, handlers }
}
